"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isConsolidatedAmountSafe = exports.isValidDATEVCreditorNumber = exports.isExpenseSafeForDATEVExport = exports.isExpenseDATEVCompliant = exports.isExpenseAccountCodeDATEVCompliant = exports.isBuCodeDATEVCompliant = void 0;
const datevConsts_1 = require("../consts/datevConsts");
const models_1 = require("../models");
const expenseSplit_model_1 = require("../models/expense/expenseSplit.model");
const common_1 = require("./common");
const expense_utils_1 = require("./expense.utils");
exports.isBuCodeDATEVCompliant = (buCode) => {
    if (!buCode)
        return false;
    if (!datevConsts_1.ALLOWED_DATEV_BU_CODES.includes(Number(buCode)))
        return false;
    // max 4 numeric digits, no leading zero
    if (!/^[1-9][0-9]{0,3}$/.test(buCode))
        return false;
    return true;
};
exports.isExpenseAccountCodeDATEVCompliant = (expenseAccountCode) => {
    if (!expenseAccountCode)
        return false;
    const numeric = common_1.isNumeric(expenseAccountCode);
    const doesNotHaveDecimalNumbers = Number(expenseAccountCode) % 1 === 0;
    const isAtLeastOne = Number(expenseAccountCode) >= 1;
    const noLeadingZeros = expenseAccountCode.length > 0 && expenseAccountCode.substring(0, 1) !== '0';
    const max8Digits = expenseAccountCode.length < 9;
    const noCommaOrDot = !expenseAccountCode.includes(',') && !expenseAccountCode.includes('.');
    return numeric && isAtLeastOne && noLeadingZeros && max8Digits && doesNotHaveDecimalNumbers && noCommaOrDot;
};
exports.isExpenseDATEVCompliant = (expense, taxes, expenseAccounts, checks = { taxes: true, expenseAccounts: true, amounts: true }) => {
    const isReportingOnly = expense.paymentFlowInformation === models_1.ExpensePaymentFlowInformationEnum.REPORTING_ONLY;
    const isNotReviewedYet = !expense_utils_1.isAtLeastReviewed(expense.status);
    const usedTaxes = [
        taxes.find(({ _id }) => { var _a; return (_id === null || _id === void 0 ? void 0 : _id.toString()) === ((_a = expense.taxRate) === null || _a === void 0 ? void 0 : _a.toString()); }),
        ...expense.splits.filter((split) => split.taxRate).map((split) => taxes.find(({ _id }) => { var _a; return (_id === null || _id === void 0 ? void 0 : _id.toString()) === ((_a = split.taxRate) === null || _a === void 0 ? void 0 : _a.toString()); })),
    ].filter((tax) => !!tax);
    // BU Code Check is ignored for mileage or perdiem expense
    const hasIncompatibleTaxBuCodes = checks.taxes && !expense_utils_1.isMileageOrPerDiem(expense) && usedTaxes.some((tax) => (tax === null || tax === void 0 ? void 0 : tax.buCode) && !exports.isBuCodeDATEVCompliant(tax.buCode));
    const usedExpenseAccounts = [
        expenseAccounts.find(({ _id }) => { var _a; return (_id === null || _id === void 0 ? void 0 : _id.toString()) === ((_a = expense.expenseAccount) === null || _a === void 0 ? void 0 : _a.toString()); }),
        ...expense.splits.filter((split) => split.expenseAccount).map((split) => expenseAccounts.find(({ _id }) => (_id === null || _id === void 0 ? void 0 : _id.toString()) === split.expenseAccount.toString())),
    ].filter((expenseAccount) => !!expenseAccount);
    const hasIncompatibleExpenseAccountCodes = checks.expenseAccounts && usedExpenseAccounts.some((expenseAccount) => !exports.isExpenseAccountCodeDATEVCompliant(expenseAccount === null || expenseAccount === void 0 ? void 0 : expenseAccount.accountCode));
    const hasZeroGrossAmount = (checks.amounts && expense.totalGrossPrice === 0) || expense.splits.some((split) => split.grossPrice === 0 && split.kind !== expenseSplit_model_1.ItemSplitKindEnum.TIP);
    const isConsolidatedAmountUnsafe = !exports.isExpenseSafeForDATEVExport(expense);
    const checksResults = { isReportingOnly, isNotReviewedYet, hasIncompatibleTaxBuCodes, hasIncompatibleExpenseAccountCodes, hasZeroGrossAmount, isConsolidatedAmountUnsafe };
    const isCompliant = !(isReportingOnly || isNotReviewedYet || hasIncompatibleTaxBuCodes || hasIncompatibleExpenseAccountCodes || hasZeroGrossAmount || isConsolidatedAmountUnsafe);
    return {
        isCompliant,
        checks: checksResults,
    };
};
/**
 * If split is manually adjusted, we have to make sure that the adjustments that the user has made is safe for export.
 * Because if its not manually adjusted, then in the DATEV export, there is an automatic adjustment mechanism to correct rounding errors
 */
exports.isExpenseSafeForDATEVExport = (expense) => !expense.splitManuallyAdjusted || exports.isConsolidatedAmountSafe(expense);
exports.isValidDATEVCreditorNumber = (value) => {
    if (value) {
        value = String(value);
        if (!common_1.isNumeric(value))
            return false;
        if (value.length > 0 && value.substr(0, 1) === '0')
            return false;
        if (value.length < 5)
            return false;
        if (value.length > 9)
            return false;
    }
    return true;
};
/**
 * Consolidated amount is a field in DATEV that should be the expense's gross amount.
 *
 * This function checks the splits if the total gross amount of the splits is equal to the expense gross amount.
 * All calculations are done down to the 2nd decimal.
 *
 * Sometimes there are small rounding errors that might cause the total splits to deviate by 0.01 from the expense gross amount, hence dinero is utilized.
 *
 * Another possibility of unsafe consolidated amount is due to user error that they made themselves on the splits.
 */
exports.isConsolidatedAmountSafe = (expense) => {
    const target = common_1.toDinero(expense.totalGrossPrice);
    let splitTotal = common_1.toDinero(0);
    for (const split of expense.splits) {
        splitTotal = splitTotal.add(common_1.toDinero(split.grossPrice));
    }
    return target.equalsTo(splitTotal);
};
